import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import { TopNav } from '../components/top-nav.js';
import type { AdAccountListEnvelope, Brand, fetchMe } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { adAccountsQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// Brand settings layout (s178.7k / refined s178.7l). Owns:
//   - common loader (me, brand, brands, ad-accounts) so each sub-
//     route reads cached data without re-fetching.
//   - the rail — vertical list of mag-voice section links with a
//     small hand-drawn glyph per item.
//   - <Outlet /> for the active sub-route.
//
// Sub-routes under /brand/$slug/settings/<section> pull the parent
// loader via getRouteApi.

// Brand-definition sections (identity, brief, visuals, site context,
// assets) moved to the Brand hub at /brand/$slug/* (B2 — see
// the brand hub design doc (CommitMind)). Templates surface deleted entirely (B3)
// — picking layouts is Mag's job, not the founder's; the schema
// field stays with safe defaults. Settings now carries only platform-
// touching ops controls.
type SubRouteSlug = 'communication' | 'notifications' | 'authority' | 'ai-tool' | 'danger';

interface RailItem {
  slug: SubRouteSlug;
  label: string;
  subtitle: string;
}

// Mag-voice subtitle per rail item — one line, lowercase, dry.
// Promoted from the bottom-of-page card descriptions in the old
// single-scroll layout so picking the section feels deliberate
// rather than scanning a long page.
// [2026-06-08] Settings rail collapsed from 6 items to 3. integrations
// + spend + strategy got removed:
//   - integrations duplicated /channels
//   - spend was brand-wide but the real lever is per-campaign
//   - strategy split: country/timezone → onboarding checklist
//     (autodetect + confirm), heartbeats → /home left lane
type T = ReturnType<typeof useLingui>['t'];

// Built at render, not at module level: a module-level t`` evaluates
// before any locale is active and bakes English in for the process. The
// `slug`s are route segments and stay as they are.
function railItems(t: T): RailItem[] {
  return [
    // [2026-09-30] Split out of notifications (spec d9d347f4). One pane
    // was answering two questions — WHERE mag reaches you and WHAT she
    // reaches you about — in 883 lines. Communication comes first
    // because the matrix can only narrow what the default channel here
    // already allows; deciding "which messages" before "which line" is
    // the order that made the old page confusing.
    {
      slug: 'communication',
      label: i18n._(msg`communication channel`),
      subtitle: i18n._(msg`where i reach you.`),
    },
    {
      slug: 'notifications',
      label: i18n._(msg`notifications`),
      subtitle: i18n._(msg`what i tell you about.`),
    },
    // s215 phase 3a — per-decision-type authority map. Drops
    // accumulated from mistakes show up here as ↘ marks.
    {
      slug: 'authority',
      label: i18n._(msg`authority`),
      subtitle: i18n._(msg`what i can do without asking.`),
    },
    // [spec §13.8] The MCP side door. Its own pane rather than a card on
    // communication: that pane is about how mag reaches the founder, this is
    // the founder reaching mag from somewhere else, and conflating two
    // questions in one pane is what forced the communication/notifications
    // split in the first place.
    {
      slug: 'ai-tool',
      label: i18n._(msg`your ai tool`),
      subtitle: i18n._(msg`ask me things from claude or cursor.`),
    },
    {
      slug: 'danger',
      label: i18n._(msg`danger zone`),
      subtitle: i18n._(msg`pause or delete this brand.`),
    },
  ];
}

export const Route = createFileRoute('/brand/$slug/settings')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const accounts = await queryClient.ensureQueryData(adAccountsQuery(brand.id));
    return { me, brand, brands, accounts };
  },
  loader: ({ context }) =>
    context as {
      me: Awaited<ReturnType<typeof fetchMe>>;
      brand: Brand;
      brands: Brand[];
      accounts: AdAccountListEnvelope | null;
    },
  component: BrandSettingsLayout,
});

function BrandSettingsLayout() {
  const { brand } = Route.useLoaderData();
  useBrandTitle(brand.slug, ['settings']);

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="settings" />
      <div className="mx-auto max-w-[1080px] px-5 pb-20 pt-7 sm:px-8">
        <SettingsHeader brandName={brand.name} />
        <div className="mt-7 grid grid-cols-1 gap-7 md:grid-cols-[224px_1fr] md:gap-10">
          <SettingsRail brandSlug={brand.slug} />
          <section className="min-w-0">
            <PaneHeader />
            <Outlet />
          </section>
        </div>
      </div>
    </main>
  );
}

function SettingsHeader({ brandName }: { brandName: string }) {
  // Compressed page header. The rail tells the founder which section
  // they're in; the page header just frames "you're in settings for
  // <brand>." Dropped the decorative ✦ chip — it didn't carry info
  // and competed visually with the per-pane header.
  return (
    <header>
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>settings</Trans>
      </p>
      <h1 className="mt-0.5 font-display text-[22px] font-black leading-tight tracking-[-0.03em] text-[var(--ink)] sm:text-[24px]">
        {brandName.toLowerCase()}.
      </h1>
    </header>
  );
}

function SettingsRail({ brandSlug }: { brandSlug: string }) {
  const { t } = useLingui();
  return (
    <aside>
      <nav className="flex flex-col gap-1 md:sticky md:top-20">
        {railItems(t).map((item) => (
          <RailLink
            key={item.slug}
            slug={item.slug}
            label={item.label}
            subtitle={item.subtitle}
            brandSlug={brandSlug}
          />
        ))}
      </nav>
    </aside>
  );
}

function RailLink({
  slug,
  label,
  subtitle,
  brandSlug,
}: {
  slug: SubRouteSlug;
  label: string;
  subtitle: string;
  brandSlug: string;
}) {
  // tanstack's Link wants a literal-typed `to`. The slug type covers
  // our 8 known sections; the assertion threads it without losing
  // safety on either side.
  const to = `/brand/$slug/settings/${slug}` as const;
  // base = inactive; active card is applied via activeProps so the
  // selected route reads as a clear chiclet (spell-yellow chiclet,
  // ink border, plum shadow + a right arrow).
  const inactive = [
    'group flex items-center gap-2.5 rounded-md',
    'border border-transparent px-2.5 py-2',
    'no-underline transition-all duration-150',
    'hover:border-[var(--line)] hover:bg-white',
  ].join(' ');
  const active = [
    'group flex items-center gap-2.5 rounded-md',
    'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] px-2.5 py-2',
    'no-underline shadow-[2px_2px_0_var(--plum)] transition-all duration-150',
  ].join(' ');
  return (
    <Link
      to={to}
      params={{ slug: brandSlug }}
      className={inactive}
      activeProps={{ className: active }}
    >
      <RailGlyph slug={slug} />
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[13px] font-semibold leading-tight text-[var(--ink)]">
          {label}
        </span>
        <span className="mt-0.5 block text-[11px] leading-snug text-[var(--ink)]/55">
          {subtitle}
        </span>
      </span>
      <span
        aria-hidden="true"
        className="font-mono text-[12px] text-[var(--ink)]/0 transition-opacity duration-150 group-data-[status=active]:text-[var(--ink)]/70"
      >
        →
      </span>
    </Link>
  );
}

/** Small hand-drawn line glyph per section. Same line weight and
 *  corner rounding as the channel glyphs in the matrix so the two
 *  surfaces share an aesthetic. Decorative — rail label carries
 *  the accessible text. */
function RailGlyph({ slug }: { slug: SubRouteSlug }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': 'true' as const,
    className: 'shrink-0 text-[var(--ink)]/65 group-data-[status=active]:text-[var(--ink)]',
  };
  switch (slug) {
    case 'communication':
      // Signal arcs — the line mag reaches you on. Added with the rail
      // split; the switch fell through to `default: return null` for a
      // release, which is why this item rendered with an empty gap
      // where every sibling had a mark.
      return (
        <svg {...common}>
          <title>
            <Trans>communication channel</Trans>
          </title>
          <circle cx="8" cy="11.5" r="1.1" fill="currentColor" stroke="none" />
          <path d="M5.6 9.4a3.4 3.4 0 0 1 4.8 0" />
          <path d="M3.4 7a6.5 6.5 0 0 1 9.2 0" />
        </svg>
      );
    case 'notifications':
      // Sparkle — mag-voice mark. (It used to be justified as "how mag
      // reaches you"; that is the communication item's job now, and this
      // one is about which messages get made at all.)
      return (
        <svg {...common}>
          <title>
            <Trans>notifications</Trans>
          </title>
          <path d="M8 2.5v3M8 10.5v3M2.5 8h3M10.5 8h3M4.2 4.2l1.6 1.6M10.2 10.2l1.6 1.6M4.2 11.8l1.6-1.6M10.2 5.8l1.6-1.6" />
        </svg>
      );
    case 'authority':
      // Stamp/seal — authority is about which decisions mag can
      // make without asking. A scallop-edged circle around a check
      // reads as "approval mark."
      return (
        <svg {...common}>
          <title>
            <Trans>authority</Trans>
          </title>
          <circle cx="8" cy="8" r="5.2" />
          <path d="m5.7 8.3 1.6 1.6 3-3.4" />
        </svg>
      );
    case 'ai-tool':
      // A window with a prompt caret — someone else's app, with mag answering
      // inside it. Deliberately NOT a sparkle: notifications already owns that
      // mark, and two sparkles in one rail reads as the same thing twice.
      return (
        <svg {...common}>
          <title>
            <Trans>your ai tool</Trans>
          </title>
          <rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1.6" />
          <path d="M5 7.1 6.7 8.6 5 10.1" />
          <path d="M8.6 10.2h2.6" />
        </svg>
      );
    case 'danger':
      // Triangle with !.
      return (
        <svg {...common}>
          <title>
            <Trans>danger zone</Trans>
          </title>
          <path d="m8 2.5 5.5 10h-11Z" />
          <path d="M8 6.5v3M8 11h.01" />
        </svg>
      );
    default:
      return null;
  }
}

/** Pane header — single source of truth for the [✦] + ↘ <section> +
 *  headline pattern that lives at the top of every settings sub-route.
 *  Reads the active slug off the URL so each sub-route gets the right
 *  header without having to render its own (and without duplicating).
 *
 *  The headline is the same mag-voice line that appears as the rail
 *  subtitle — they reinforce each other ("oh, that's where i set X"). */
function PaneHeader() {
  const { t } = useLingui();
  const location = useLocation();
  // Last URL segment is the section slug. Defaults to identity for
  // the bare `/settings` path (which the index route redirects, but
  // belt-and-suspenders).
  const segments = location.pathname.split('/').filter(Boolean);
  const slug = (segments[segments.length - 1] ?? 'identity') as string;
  const item = railItems(t).find((i) => i.slug === slug);
  if (!item) return null;
  return (
    <header className="mb-5">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        ↘ {item.label}
      </p>
      <h2 className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
        {item.subtitle}
      </h2>
    </header>
  );
}

/** Shared mutation-aftermath helper. Each sub-route imports this so
 *  rename / logo / archive mutations re-run the layout's loader (and
 *  refresh the brands cache that other surfaces read). Lives here
 *  because the layout is the route that owns the brand data. */
export function useRefreshBrand() {
  const router = useRouter();
  const queryClient = useQueryClient();
  return async () => {
    await refreshQueries(queryClient, ['brands']);
    await router.invalidate();
  };
}
